import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../services/api';
import { useApp } from '../context/AppContext';
import { Badge } from '../components/Badge';
import { StatCard } from '../components/StatCard';
import { formatDate } from '../utils/format';

const emptyForm = {
  employee_id: '',
  employee_name: '',
  date: new Date().toISOString().slice(0, 10),
  check_in: '09:00',
  check_out: '18:00',
  status: 'present',
  hours: 8,
  notes: '',
  source: 'manual',
};

function statusVariant(s) {
  if (['present', 'late', 'absent', 'remote', 'leave'].includes(s)) return s;
  return 'pending';
}

export function Attendance() {
  const { t } = useApp();
  const [searchParams] = useSearchParams();
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);
  const [filterDate, setFilterDate] = useState('');
  const [filterEmployee, setFilterEmployee] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [todayRows, setTodayRows] = useState([]);

  useEffect(() => {
    const eid = searchParams.get('employee_id');
    if (eid) setFilterEmployee(eid);
  }, [searchParams]);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(false);
    try {
      const params = {};
      if (filterDate) params.date = filterDate;
      if (filterEmployee) params.employee_id = filterEmployee;
      if (filterStatus) params.status = filterStatus;
      const todayStr = new Date().toISOString().slice(0, 10);
      const [sRes, rRes, tRes, eRes] = await Promise.all([
        api.get('/api/attendance/summary'),
        api.get('/api/attendance', { params }),
        api.get('/api/attendance', { params: { date: todayStr } }),
        api.get('/api/employees'),
      ]);
      setSummary(sRes.data);
      setRows(Array.isArray(rRes.data) ? rRes.data : []);
      setTodayRows(Array.isArray(tRes.data) ? tRes.data : []);
      setEmployees(Array.isArray(eRes.data) ? eRes.data : []);
    } catch (e) {
      console.error(e);
      setErr(true);
      setSummary(null);
      setRows([]);
      setTodayRows([]);
    } finally {
      setLoading(false);
    }
  }, [filterDate, filterEmployee, filterStatus]);

  useEffect(() => {
    load();
  }, [load]);

  const remoteToday = useMemo(() => todayRows.filter((r) => r.status === 'remote').length, [todayRows]);

  const weekStats = useMemo(() => {
    const cutoff = new Date();
    cutoff.setHours(0, 0, 0, 0);
    cutoff.setDate(cutoff.getDate() - 7);
    const list = rows.filter((r) => {
      const d = new Date(r.date);
      return !Number.isNaN(d.getTime()) && d >= cutoff;
    });
    const hours = list.reduce((acc, r) => acc + (Number(r.hours) || 0), 0);
    return { count: list.length, hours: Math.round(hours * 10) / 10 };
  }, [rows]);

  const employeeOptions = useMemo(
    () => [...employees].sort((a, b) => `${a.last_name}`.localeCompare(b.last_name)),
    [employees]
  );

  function pickEmployee(id) {
    const e = employees.find((x) => x.id === id);
    if (!e) return;
    setForm((f) => ({
      ...f,
      employee_id: e.id,
      employee_name: `${e.first_name} ${e.last_name}`.trim(),
    }));
  }

  async function submitCreate() {
    try {
      await api.post('/api/attendance', {
        employee_id: form.employee_id,
        employee_name: form.employee_name,
        date: form.date,
        check_in: form.check_in || null,
        check_out: form.check_out || null,
        status: form.status,
        hours: Number(form.hours) || 0,
        notes: form.notes || null,
        source: form.source || 'manual',
      });
      setModal(false);
      setForm(emptyForm);
      await load();
    } catch (e) {
      console.error(e);
    }
  }

  async function quickDemoRow() {
    const e = employees[0];
    if (!e) return;
    try {
      await api.post('/api/attendance', {
        employee_id: e.id,
        employee_name: `${e.first_name} ${e.last_name}`,
        date: new Date().toISOString().slice(0, 10),
        check_in: '09:15',
        check_out: '18:00',
        status: 'late',
        hours: 8.5,
        notes: t('attendance.demoNote'),
        source: 'demo',
      });
      await load();
    } catch (err2) {
      console.error(err2);
    }
  }

  if (loading && !summary && rows.length === 0) {
    return <div className="empty-state">{t('common.loading')}</div>;
  }

  return (
    <div className="page-stack attendance-page attendance-page--v15">
      <header className="att-hero panel panel--elevated">
        <h1 className="att-hero__title">{t('attendance.title')}</h1>
        <p className="att-hero__lead">{t('attendance.subtitle')}</p>
      </header>

      {err && <div className="banner banner--info">{t('common.error')}</div>}

      {summary && (
        <div className="grid-stats grid-stats--attendance-kpis att-kpi-row">
          <StatCard icon="✓" label={t('attendance.kpiPresent')} value={summary.present_today ?? 0} />
          <StatCard icon="⏰" tone="warning" label={t('attendance.kpiLate')} value={summary.late_today ?? 0} />
          <StatCard icon="○" label={t('attendance.kpiAbsent')} value={summary.absent_today ?? 0} />
          <StatCard icon="⌁" tone="accent" label={t('attendance.kpiRemote')} value={remoteToday} />
          <StatCard icon="∑" tone="accent" label={t('attendance.kpiWeekHours')} value={summary.estimated_week_hours ?? 0} />
          <StatCard icon="◎" label={t('attendance.kpiCompliance')} value="94%" tone="success" />
        </div>
      )}

      <section className="panel panel--elevated att-insights-pro">
        <h2 className="panel__title">{t('attendance.insightsTitle')}</h2>
        <ul className="pi-card-list">
          <li>{t('attendance.insightLate')}</li>
          <li>{t('attendance.insightArea')}</li>
          <li>{t('attendance.insightHours')}</li>
        </ul>
        <p className="panel__muted text-sm">{t('attendance.clockHint')}</p>
      </section>

      <div className="toolbar toolbar--card attendance-toolbar">
        <div className="form-row form-row--filters">
          <label className="field field--inline">
            <span>{t('attendance.filterDate')}</span>
            <input className="input" type="date" value={filterDate} onChange={(e) => setFilterDate(e.target.value)} />
          </label>
          <label className="field field--inline">
            <span>{t('attendance.filterEmployee')}</span>
            <select className="input" value={filterEmployee} onChange={(e) => setFilterEmployee(e.target.value)}>
              <option value="">{t('attendance.allEmployees')}</option>
              {employeeOptions.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.first_name} {e.last_name}
                </option>
              ))}
            </select>
          </label>
          <label className="field field--inline">
            <span>{t('attendance.filterStatus')}</span>
            <select className="input" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
              <option value="">{t('attendance.allStatuses')}</option>
              {['present', 'late', 'absent', 'remote', 'leave'].map((s) => (
                <option key={s} value={s}>
                  {t(`attendance.status.${s}`)}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="toolbar__grow" />
        <button type="button" className="btn btn--secondary" onClick={quickDemoRow} disabled={!employees.length}>
          {t('attendance.quickDemo')}
        </button>
        <button type="button" className="btn btn--primary" onClick={() => setModal(true)}>
          {t('attendance.newRecord')}
        </button>
      </div>

      <p className="panel__muted text-sm">{t('attendance.futureHint')}</p>

      <div className="table-wrap table-wrap--rounded">
        <table className="data-table">
          <thead>
            <tr>
              <th>{t('attendance.colEmployee')}</th>
              <th>{t('attendance.colDate')}</th>
              <th>{t('attendance.colIn')}</th>
              <th>{t('attendance.colOut')}</th>
              <th>{t('attendance.colStatus')}</th>
              <th>{t('attendance.colHours')}</th>
              <th>{t('attendance.colNotes')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={8} className="empty-inline">
                  {t('attendance.empty')}
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr key={r.id}>
                  <td>
                    <strong>{r.employee_name}</strong>
                    <div className="text-muted text-sm mono">{r.employee_id}</div>
                  </td>
                  <td>{formatDate(r.date)}</td>
                  <td>{r.check_in ?? '—'}</td>
                  <td>{r.check_out ?? '—'}</td>
                  <td>
                    <Badge variant={statusVariant(r.status)}>{t(`attendance.status.${r.status}`) || r.status}</Badge>
                  </td>
                  <td>{r.hours}</td>
                  <td className="cell-truncate" title={r.notes || ''}>
                    {r.notes || '—'}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn btn--sm btn--ghost"
                      onClick={async () => {
                        if (!window.confirm(t('attendance.confirmDelete'))) return;
                        try {
                          await api.delete(`/api/attendance/${r.id}`);
                          await load();
                        } catch (e2) {
                          console.error(e2);
                        }
                      }}
                    >
                      {t('attendance.delete')}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <section className="panel panel--elevated att-week-panel">
        <h2 className="panel__title">{t('attendance.weekSummaryTitle')}</h2>
        <p className="panel__muted text-sm">{t('attendance.weekSummaryHint')}</p>
        {weekStats.count === 0 ? (
          <p className="panel__muted">{t('attendance.weekSummaryEmpty')}</p>
        ) : (
          <div className="att-week-stats">
            <div>
              <span className="att-week-stats__label">{t('attendance.colHours')}</span>
              <strong className="att-week-stats__value">{weekStats.hours} h</strong>
            </div>
            <div>
              <span className="att-week-stats__label">{t('attendance.colEmployee')}</span>
              <strong className="att-week-stats__value">{weekStats.count}</strong>
            </div>
          </div>
        )}
      </section>

      {modal && (
        <div className="modal-backdrop" role="dialog" aria-modal>
          <div className="modal modal--wide">
            <h2 className="modal__title">{t('attendance.modalTitle')}</h2>
            <div className="form-grid">
              <label className="field field--full">
                <span>{t('attendance.colEmployee')}</span>
                <select
                  className="input"
                  value={form.employee_id}
                  onChange={(e) => {
                    const id = e.target.value;
                    pickEmployee(id);
                  }}
                >
                  <option value="">{t('attendance.pickEmployee')}</option>
                  {employeeOptions.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.first_name} {e.last_name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field">
                <span>{t('attendance.colDate')}</span>
                <input className="input" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
              </label>
              <label className="field">
                <span>{t('attendance.colStatus')}</span>
                <select className="input" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                  {['present', 'late', 'absent', 'remote', 'leave'].map((s) => (
                    <option key={s} value={s}>
                      {t(`attendance.status.${s}`)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field">
                <span>{t('attendance.colIn')}</span>
                <input className="input" type="time" value={form.check_in} onChange={(e) => setForm({ ...form, check_in: e.target.value })} />
              </label>
              <label className="field">
                <span>{t('attendance.colOut')}</span>
                <input className="input" type="time" value={form.check_out} onChange={(e) => setForm({ ...form, check_out: e.target.value })} />
              </label>
              <label className="field">
                <span>{t('attendance.colHours')}</span>
                <input
                  className="input"
                  type="number"
                  step="0.25"
                  value={form.hours}
                  onChange={(e) => setForm({ ...form, hours: e.target.value })}
                />
              </label>
              <label className="field field--full">
                <span>{t('attendance.colNotes')}</span>
                <input className="input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </label>
            </div>
            <div className="modal__actions">
              <button type="button" className="btn btn--ghost" onClick={() => setModal(false)}>
                {t('employees.cancel')}
              </button>
              <button type="button" className="btn btn--primary" onClick={submitCreate} disabled={!form.employee_id}>
                {t('employees.save')}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
